/**
 * IMPORTANT:
 * This helper defines the canonical embed behavior for:
 * - editor
 * - preview
 * - published site
 *
 * Do not reimplement embed URL logic elsewhere.
 * All components must use this helper.
 *
 * Backend counterpart: webbuilder-api/src/services/componentRenderers/renderUtils.ts
 */

export type VideoSourceType = 'upload' | 'youtube' | 'vimeo';
export type EmbedProvider = 'youtube' | 'vimeo';

export const EMBED_ASPECT_RATIO = '16:9';
export const EMBED_ASPECT_RATIO_CSS = '16/9';

export const isEmbedProvider = (sourceType?: string): sourceType is EmbedProvider =>
  sourceType === 'youtube' || sourceType === 'vimeo';

export const parseYouTubeUrl = (url: string): string | null => {
  if (!url) return null;

  if (/^[a-zA-Z0-9_-]{11}$/.test(url)) {
    return url;
  }

  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtube\.com\/watch\?.+&v=)([a-zA-Z0-9_-]{11})/,
    /youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) return match[1];
  }

  return null;
};

export const parseVimeoUrl = (url: string): string | null => {
  if (!url) return null;

  if (/^\d+$/.test(url)) {
    return url;
  }

  const patterns = [/vimeo\.com\/(\d+)/, /player\.vimeo\.com\/video\/(\d+)/];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) return match[1];
  }

  return null;
};

const sanitizeEmbedId = (provider: EmbedProvider, rawId: string): string => {
  const id = (rawId || '').trim();
  if (provider === 'youtube') {
    return id.replace(/[^a-zA-Z0-9_-]/g, '');
  }
  return id.replace(/[^0-9]/g, '');
};

export const getEmbedSrc = (provider: EmbedProvider, rawId: string): string | null => {
  const embedId = sanitizeEmbedId(provider, rawId);
  if (!embedId) return null;

  if (provider === 'youtube') {
    return `https://www.youtube-nocookie.com/embed/${embedId}?rel=0&modestbranding=1&iv_load_policy=3`;
  }

  return `https://player.vimeo.com/video/${embedId}?byline=0&portrait=0&title=0`;
};
